<template>
    <div v-show="ready" class="v-sync-graph" />
</template>
<style scoped lang="scss"></style>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { ngInject } from '@/vue/decorators/angular-interop';
import { defaultsDeep } from 'lodash';
import Capabilities from '@/utils/capabilities';

@Component({
    emits: ['mouse-enter', 'mouse-move', 'mouse-leave', 'graph-ready', 'tap'],
})
export default class VrmInstallationsTableChart extends Vue {
    /** @type {string} */
    @Prop()
    blockState;
    /** @type {number} */
    @Prop()
    idSite;
    /** @type {string} */
    @Prop()
    attribute;
    /** @type {number} */
    @Prop({ type: Number, default: null })
    positionHover;

    @ngInject()
    installationsTableGraph;
    @ngInject()
    GraphService;
    @ngInject()
    highchartsNG;
    @ngInject()
    ChartLoaderService;

    resizeObserver = null;

    isHovered = null;
    isHovering = null;
    highcharts = null;
    chart = null;
    options = null;
    /** @type {null|{idSite:number,attribute:string}} */
    pending = null;
    ready = false;

    get dataDependencies() {
        return [this.idSite, this.attribute];
    }

    delay = null;
    @Watch('dataDependencies', { immediate: true })
    onDepsChanged() {
        if (this.delay) {
            clearTimeout(this.delay);
        }
        this.ready = false;
        this.delay = setTimeout(() => this.updateChart(), 1000);
    }

    @Watch('dataDependencies', { immediate: true })
    async updateChart() {
        // if (!this.testme) return;
        const { idSite, attribute } = this;
        if (this.pending) {
            if (this.pending.idSite === idSite && this.pending.attribute === attribute) {
                // Skip logic if the pending fetch is the same as the scheduled one
                this.ready = true;
                return;
            } else {
                // Target changed, cancel ongoing call and restart
                this.releaseDataLoad();
            }
        }
        // State: The inputs have changed and a fetch is required
        this.ready = false;
        // Store inputs to compare on changes
        this.pending = {
            idSite,
            attribute,
        };

        // Request graph data
        let data;
        try {
            data = await this.ChartLoaderService.queueGraphDataLoad(idSite, attribute);
        } catch (error) {
            this.pending = null;
            return;
        }

        if (!this.pending || this.pending.idSite !== idSite || this.pending.attribute !== attribute)
            return;

        await this.GraphService.ready();
        const baseConfig = this.installationsTableGraph();

        // Prepare data for chart
        const seriesData = data.stats[attribute]
            ? data.stats[attribute].map(values => {
                  // For the Load/Consumption column, use max values instead of means.
                  if (this.attribute === 'consumption') {
                      return [values[0], values[3], values[2], values[1]];
                  }
                  return values[2]
                      ? [values[0], values[1], values[2], values[3]]
                      : [values[0], values[1]];
              })
            : [];

        let seriesObject = {
            data: seriesData,
            kdNow: true,
        };

        if (Capabilities.hasTouchSupport()) {
            seriesObject['enableMouseTracking'] = false;
        }

        // Build config pieces
        const seriesConfig = {
            series: [seriesObject],
        };

        const renderTo = {
            chart: {
                renderTo: this.$el,
            },
        };

        const sizeConfig = {
            chart: baseConfig.size,
        };

        const colorConfig = {
            plotOptions: {
                area: {
                    color: this.chartColor,
                    fillColor: this.chartColor.fillColor,
                    lineColor: this.chartColor.lineColor,
                    fillOpacity: 1,
                    dataLabels: {
                        enabled: false,
                    },
                },
                series: {
                    stickyTracking: true,
                    marker: {
                        enabled: false,
                        enabledThreshold: 10,
                    },
                    states: {
                        normal: {
                            marker: {
                                enabled: false,
                            },
                        },
                        hover: {
                            area: this.hoverColor,
                            halo: {
                                size: 7,
                                opacity: 1,
                                attributes: {
                                    fill: this.hoverColor,
                                    'stroke-width': 2,
                                    stroke: this.hoverColor,
                                },
                            },
                            lineColor: this.lineColor,
                        },
                    },
                    point: {},
                },
            },
        };
        // Merge configs, most-to-least important
        const options = defaultsDeep(
            seriesConfig,
            renderTo,
            colorConfig,
            baseConfig,
            sizeConfig,
            baseConfig.options,
        );
        if (!this.chart) {
            this.highcharts = await this.highchartsNG.getHighcharts();
            this.chart = new this.highcharts.Chart(options, null);
        } else {
            this.chart.update(options);
        }
        this.ready = true;

        this.$emit('graph-ready', {
            idSite: this.idSite,
            chart: this.chart,
            attribute: this.attribute,
        });
    }

    mounted() {
        this.$el.addEventListener('mouseenter', this.onMouseOver);
        this.$el.addEventListener('mouseleave', this.onMouseOut);
        this.$el.addEventListener('mousemove', this.throttle(this.onMouseMove, 100));

        if (Capabilities.hasTouchSupport()) {
            this.$el.addEventListener('click', this.onTap);
        }

        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.$el);
    }

    onResize() {
        if (this.chart) {
            this.chart.reflow();
        }
    }

    throttle(mainFunction, delay) {
        let timerFlag = null; // Variable to keep track of the timer

        // Returning a throttled version
        return (...args) => {
            if (timerFlag === null) {
                // If there is no timer currently running
                mainFunction(...args); // Execute the main function
                timerFlag = setTimeout(() => {
                    // Set a timer to clear the timerFlag after the specified delay
                    timerFlag = null; // Clear the timerFlag to allow the main function to be executed again
                }, delay);
            }
        };
    }

    releaseDataLoad() {
        this.ChartLoaderService.releaseDataLoad(this.pending.idSite, this.pending.attribute);
        this.pending = null;
    }

    beforeDestroy() {
        if (this.pending) {
            this.releaseDataLoad();
        }
        if (this.chart) {
            this.chart.destroy();
        }

        if (this.resizeObserver) {
            this.resizeObserver.unobserve(this.$el);
        }
    }

    get lineColor() {
        let darkMode = document.getElementsByTagName('html')[0].className;
        if (darkMode) {
            return '#000000';
        } else {
            return '#FFFFFF';
        }
    }

    get yValue() {
        console.log(this.chart.series.data.y);
        this.$emit('yValue', this.chart.series.data.y);
        return this.chart.series.data.y;
    }

    get chartColor() {
        let darkMode = document.getElementsByTagName('html')[0].className;
        switch (this.attribute) {
            case 'bs':
                if (darkMode) {
                    return {
                        lineColor: 'rgba(56, 125, 197, 0.8)',
                        fillColor: 'rgba(71, 144, 208, 0.1)',
                    };
                } else {
                    return {
                        lineColor: '#95bcdc',
                        fillColor: '#e9f4ff',
                    };
                }
            case 'solar_yield':
                if (darkMode) {
                    return {
                        lineColor: 'rgba(240, 150, 46, 0.8)',
                        fillColor: 'rgba(247, 171, 62, 0.1)',
                    };
                } else {
                    return {
                        lineColor: '#f5ce96',
                        fillColor: '#F7AB3E1A',
                    };
                }
            case 'consumption':
                if (darkMode) {
                    return {
                        lineColor: 'rgba(114, 184, 76, 0.8)',
                        fillColor: 'rgba(114, 184, 76, 0.1)',
                    };
                } else {
                    return {
                        lineColor: '#add096',
                        fillColor: '#f1f8ed',
                    };
                }
            case 'from_to_grid':
                if (darkMode) {
                    return {
                        lineColor: 'rgba(243, 92, 88, 0.8)',
                        fillColor: 'rgba(243, 92, 88, 0.1)',
                    };
                } else {
                    return {
                        lineColor: '#f68f8c',
                        fillColor: '#feefee',
                    };
                }
            case 'generator':
                if (darkMode) {
                    return {
                        lineColor: 'rgba(114, 184, 76, 0.8)',
                        fillColor: 'rgba(114, 184, 76, 0.1)',
                    };
                } else {
                    return {
                        lineColor: '#add096',
                        fillColor: '#f1f8ed',
                    };
                }
            default:
                return {
                    lineColor: 'f1f8ed',
                    fillColor: 'f1f8ed',
                };
        }
    }

    get hoverColor() {
        switch (this.attribute) {
            case 'bs':
                return '#387DC5';
            case 'solar_yield':
                return '#F0962E';
            case 'consumption':
                return '#72B84C';
            case 'generator':
                return '#72B84C';
            default:
                return '#F35C58';
        }
    }

    onMouseOver(e) {
        if (this.chart) {
            this.$emit('mouse-enter', this.buildBaseEvent(e, this.findSrcPoint(e)));
        }
    }

    onMouseOut(e) {
        if (this.chart) {
            this.$emit('mouse-leave', this.buildBaseEvent(e, this.findSrcPoint(e)));
        }
    }

    onMouseMove(e) {
        if (this.chart) {
            this.$emit('mouse-move', this.buildBaseEvent(e, this.findSrcPoint(e)));
        }
    }

    /**
     * Handle taps/clicks on the chart.
     *
     * Finds and emits the series data point along with the event if it exists.
     *
     * @param e - Browser click event
     */
    onTap(e) {
        e.preventDefault();
        e.stopPropagation();

        this.$emit('tap', this.buildBaseEvent(e, this.findSrcPoint(e)));
    }

    /**
     * Build a basic event listener event object.
     *
     * Includes the idSite and attribute for this chart as well as the source event.
     *
     * @param e - Browser event object
     * @param additionalProps
     * @returns {{idSite: number, attribute: string, event}}
     */
    buildBaseEvent(e, additionalProps = {}) {
        return Object.assign(
            {},
            {
                idSite: this.idSite,
                attribute: this.attribute,
                event: e,
            },
            additionalProps || {},
        );
    }

    /**
     * Find a data point in the chart series based on a cursor event.
     *
     * @param e
     * @returns {{point: (Point|undefined), timestamp: (Number|undefined)}}
     */
    findSrcPoint(e) {
        const srcChart = this.chart;
        const event = srcChart.pointer.normalize(e);

        if (!srcChart.series.length) {
            return {
                point: undefined,
                timestamp: undefined,
            };
        }

        const srcChartPoint = srcChart.series[0].searchPoint(event, true);
        let srcTimestamp;

        if (srcChart.series[0].type === 'columnrange') {
            const leftOffset = $(srcChart.container).offset().left;
            const xCoordinate = e.clientX - srcChart.plotLeft - leftOffset;
            srcTimestamp = srcChart.yAxis[0].toValue(xCoordinate, true);
        } else {
            if (!srcChartPoint || !srcChartPoint.x) {
                return {
                    point: undefined,
                    timestamp: undefined,
                };
            }
            srcTimestamp = srcChartPoint.x;
        }

        return {
            timestamp: srcTimestamp,
            point: srcChartPoint,
        };
    }
}
</script>
